import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertTriangle, Receipt, Phone } from "lucide-react";

export default function DriverPortal() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, dRes] = await Promise.all([api.get("/dashboard/stats"), api.get("/debts")]);
        setStats(sRes.data); setDebts(dRes.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  const activeDebts = debts.filter(d => d.statut === "en_cours");
  const paidDebts = debts.filter(d => d.statut === "rembourse");

  return (
    <div className="max-w-2xl mx-auto" data-testid="driver-portal">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold font-heading text-slate-900">Bonjour, {user?.prenom}</h1>
        <p className="text-sm text-slate-500 mt-1">Consultez vos dettes et paiements</p>
      </div>

      {/* Summary Card */}
      <Card className={`mb-6 ${stats.total_dettes > 0 ? "border-red-200 bg-red-50/50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid="debt-summary">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${stats.total_dettes > 0 ? "bg-red-100" : "bg-emerald-100"}`}>
              {stats.total_dettes > 0 ? <AlertTriangle className="w-7 h-7 text-red-600" /> : <Receipt className="w-7 h-7 text-emerald-600" />}
            </div>
            <div>
              <p className="text-sm text-slate-600">{stats.total_dettes > 0 ? "Montant total des dettes" : "Felicitations !"}</p>
              <p className={`text-3xl font-bold font-heading ${stats.total_dettes > 0 ? "text-red-700" : "text-emerald-700"}`}>
                {stats.total_dettes > 0 ? `${stats.total_dettes} Frc` : "Vous etes a jour"}
              </p>
              {stats.nombre_dettes > 0 && <p className="text-xs text-slate-500 mt-1">{stats.nombre_dettes} dette(s) en cours</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Debts */}
      {activeDebts.length > 0 && (
        <Card className="mb-6" data-testid="active-debts">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-slate-900">Dettes en cours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeDebts.map(d => (
                <div key={d.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`active-debt-${d.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{d.description || d.type}</p>
                    <Badge variant="outline" className="text-xs mt-1">{d.type}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">{d.montant_restant} Frc</p>
                    <p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid Debts */}
      {paidDebts.length > 0 && (
        <Card data-testid="paid-debts">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-slate-900">Dettes remboursees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {paidDebts.map(d => (
                <div key={d.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0 opacity-60">
                  <div>
                    <p className="text-sm text-slate-700">{d.description || d.type}</p>
                    <Badge variant="outline" className="text-xs mt-1 status-actif">Rembourse</Badge>
                  </div>
                  <p className="text-sm text-slate-500 line-through">{d.montant} Frc</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Info */}
      <Card className="mt-6 bg-blue-50/50 border-blue-200" data-testid="contact-info">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-[#0F52BA]">
            <Phone className="w-5 h-5" />
            <p className="text-sm">Pour toute question, contactez votre gestionnaire.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
